import { ExternalLink, Mail, MailCheck, RotateCw, TriangleAlert } from 'lucide-react';
import { isTestModeActive, useMailSettings } from '@/hooks/useMailSettings';

/**
 * Zeigt im Bestätigen-Schritt beider Wizards (Einzellauf + Komplettmodus),
 * WOHIN die Kundenmails dieses Vorgangs gehen.
 *
 * Anlass: Bei einer Komplettwartung gingen fünf Mails an die echten
 * Kundenadressen, weil der Mail-Testmodus zu dem Zeitpunkt aus war - im
 * Wizard stand das nirgends.
 *
 * Die Aussagen hier sind am Backend geprüft (Stand Sprint 8):
 *  - Kunden-Wartungsmails entstehen NUR für Läufe mit mode=apply
 *    (run_engine/engine.py: start_run Schritt 10, finalize_run, abort_run;
 *    client_jobs.py beim Claim). Ein Dry-Run löst sie nicht aus.
 *  - Ob umgeleitet wird, entscheidet sich erst beim Versand
 *    (services/mail/sender.py) - der app_settings-Eintrag 'mail_redirect'
 *    gewinnt dabei immer über die ENV MAIL_REDIRECT_ALL_TO.
 * Deshalb behauptet der Dry-Run-Hinweis NICHT "es geht nichts raus", sondern
 * nur, dass dieser Vorgang keine Wartungsmails auslöst.
 */

export type MailTargetKind = 'loading' | 'unknown' | 'redirect' | 'live';

export interface MailTarget {
  kind: MailTargetKind;
  /** Redirect-Adresse; nur bei kind 'redirect' gesetzt. */
  address: string | null;
  /** Status neu holen, ohne den Wizard zu verlassen. */
  refetch: () => void;
}

/**
 * Wirksames Mailziel als ein Wert. Beide Bestätigen-Schritte lesen denselben
 * Query (Key 'mail','settings') - React Query dedupliziert das.
 *
 * Nicht ladbar => 'unknown' statt stillschweigend 'live'/'redirect': ein
 * unbekannter Status darf nicht wie eine Entwarnung aussehen.
 */
export function useMailTarget(): MailTarget {
  const settings = useMailSettings();
  const refetch = (): void => {
    void settings.refetch();
  };
  if (settings.isPending) return { kind: 'loading', address: null, refetch };
  if (settings.isError || !settings.data) return { kind: 'unknown', address: null, refetch };
  if (isTestModeActive(settings.data)) {
    return { kind: 'redirect', address: settings.data.redirect_address, refetch };
  }
  return { kind: 'live', address: null, refetch };
}

/**
 * Satz für die bestehende Echtlauf-Bestätigung (Checkbox). Damit ist das
 * Mailziel Teil der Zusage, die der Nutzer ohnehin abhaken muss - ohne
 * zusätzliche Hürde. null = nichts anhängen (Status lädt noch).
 */
export function mailAckSentence(
  target: MailTarget,
  customerName?: string | null
): string | null {
  const wem = customerName ? `von ${customerName}` : 'des Kunden';
  switch (target.kind) {
    case 'redirect':
      return ` Wartungsmails gehen dabei nur an die Testadresse ${target.address}.`;
    case 'live':
      return ` Die Wartungsmails gehen dabei an die echten Empfänger ${wem}.`;
    case 'unknown':
      return ' Ob Wartungsmails umgeleitet werden, ist gerade nicht abrufbar.';
    default:
      return null;
  }
}

interface MailTargetNoticeProps {
  mode: 'check' | 'apply';
  /** Kundenname für den Klartext; null = mehrere oder unbekannt. */
  customerName?: string | null;
  className?: string;
}

export function MailTargetNotice({
  mode,
  customerName,
  className,
}: MailTargetNoticeProps): JSX.Element {
  const target = useMailTarget();
  const isApply = mode === 'apply';
  const wem = customerName ? `von ${customerName}` : 'des Kunden';
  const wrapper = className ? `${className} ` : '';

  if (target.kind === 'loading') {
    return (
      <p
        className={`${wrapper}inline-flex items-center gap-2 text-xs text-muted`}
        data-testid="mail-target-notice"
        data-variant="loading"
      >
        <Mail className="size-3" aria-hidden="true" />
        Mailziel wird geprüft …
      </p>
    );
  }

  if (target.kind === 'unknown') {
    return (
      <div
        role="alert"
        data-testid="mail-target-notice"
        data-variant="unknown"
        className={`${wrapper}flex items-start gap-3 rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm`}
      >
        <TriangleAlert className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-semibold text-warning">Mailziel unbekannt</p>
          <p className="mt-1 text-xs text-muted">
            Der Status der Mail-Umleitung ließ sich nicht laden. Ob Kundenmails
            umgeleitet werden, ist damit offen - im Zweifel vor dem Start in den
            Mail-Einstellungen nachsehen.
          </p>
          <MailTargetActions target={target} />
        </div>
      </div>
    );
  }

  if (target.kind === 'redirect') {
    return (
      <div
        role="status"
        data-testid="mail-target-notice"
        data-variant="redirect"
        className={`${wrapper}flex items-start gap-3 rounded-lg border border-border bg-surface-2/40 p-3 text-sm`}
      >
        <MailCheck className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">
            Testmodus aktiv - Kundenmails gehen nur an{' '}
            <strong className="font-mono">{target.address}</strong>
          </p>
          <p className="mt-1 text-xs text-muted">
            Der Kunde bekommt nichts: alle Mails dieses Vorgangs landen ausschließlich
            in diesem Postfach.
          </p>
          <MailTargetActions target={target} />
        </div>
      </div>
    );
  }

  // kind === 'live': keine Umleitung.
  if (!isApply) {
    return (
      <div
        role="status"
        data-testid="mail-target-notice"
        data-variant="live-check"
        className={`${wrapper}flex items-start gap-3 rounded-lg border border-border bg-surface-2/40 p-3 text-sm`}
      >
        <Mail className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">
            Testmodus aus - Mails würden an die echten Kundenadressen gehen.
          </p>
          <p className="mt-1 text-xs text-muted">
            Dieser Dry-Run löst keine Wartungsmails aus: Start- und Abschluss-Mails
            entstehen nur bei Echtläufen. Andere Mails (Ankündigungen, manueller
            Versand aus dem Postausgang) sind davon unberührt.
          </p>
          <MailTargetActions target={target} />
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-testid="mail-target-notice"
      data-variant="live-apply"
      className={`${wrapper}flex items-start gap-3 rounded-lg border-2 border-danger bg-danger/10 p-4`}
    >
      <TriangleAlert className="mt-0.5 size-6 shrink-0 text-danger" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-danger">
          Mails gehen an die ECHTEN Kundenadressen
        </p>
        <p className="mt-1 text-sm">
          Der Mail-Testmodus ist aus. Dieser Echtlauf verschickt die Wartungsmails
          ohne Umleitung an die hinterlegten Empfänger {wem}.
        </p>
        <p className="mt-1 text-xs text-muted">
          Soll der Kunde nichts bekommen, zuerst den Testmodus einschalten und den
          Status hier neu laden - die Auswahl im Wizard bleibt dabei erhalten.
        </p>
        <MailTargetActions target={target} />
      </div>
    </div>
  );
}

/**
 * Einstellungen im NEUEN Tab (kein Router-Link): der Wizard-State lebt nur im
 * Component-State, eine Navigation würde Auswahl und Idempotency-Key
 * verwerfen.
 */
function MailTargetActions({ target }: { target: MailTarget }): JSX.Element {
  return (
    <p className="mt-2 flex flex-wrap items-center gap-3 text-xs">
      <a
        href="/settings/mail"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        data-testid="mail-target-settings-link"
      >
        Mail-Einstellungen öffnen (neuer Tab)
        <ExternalLink className="size-3" aria-hidden="true" />
      </a>
      <button
        type="button"
        onClick={target.refetch}
        className="inline-flex items-center gap-1 text-muted hover:text-text"
        data-testid="mail-target-refresh"
      >
        <RotateCw className="size-3" aria-hidden="true" />
        Status neu laden
      </button>
    </p>
  );
}
